import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import theme from '../theme';

// A switcher for a list of named items whose count the customer controls
// (offerings, company sections, objections). Every item stays visible by
// name at every width; nothing scrolls off the edge behind a hidden scrollbar,
// which is what the old horizontal tab bar did once names got long (five
// SouthState offerings of 30 to 42 characters overflowed a 1200 px bar).
//
// Layouts, chosen by the width of the content column (not the viewport,
// because the app sidebar collapses from 240 to 64 px) and the viewport:
//   rail       content >= RAIL_MIN_CONTENT_WIDTH: a list card beside the detail
//   narrower:  `narrow`  'pills' (default) the segmented bar wrapping onto rows
//                        'drill' the list alone; a tap opens the detail with Back
//   phone:     `phone`   'accordion' (default) one card per item, one open
//              (viewport <= ACCORDION_MAX_VIEWPORT)  'drill' as above
// Short fixed sets (offerings, company sections) read best as pills and an
// accordion; long lists (an objection library of 64) need 'drill' for both.
//
// `SegmentedBar` / `SegmentedItem` are exported for fixed tab sets that only
// need the wrapping bar (Playbook sections).

export const RAIL_MIN_CONTENT_WIDTH = 880;
export const ACCORDION_MAX_VIEWPORT = 768;

export const tierFor = ({ containerWidth, viewportWidth }) => {
  if (viewportWidth != null && viewportWidth <= ACCORDION_MAX_VIEWPORT) return 'accordion';
  if (containerWidth != null && containerWidth >= RAIL_MIN_CONTENT_WIDTH) return 'rail';
  return 'pills';
};

const useTier = (ref, forced) => {
  const [size, setSize] = useState({ containerWidth: null, viewportWidth: typeof window !== 'undefined' ? window.innerWidth : null });
  useEffect(() => {
    if (forced) return undefined;
    const el = ref.current;
    const measure = () => setSize({
      containerWidth: el ? el.getBoundingClientRect().width : null,
      viewportWidth: window.innerWidth,
    });
    measure();
    let observer = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, forced]);
  return forced || tierFor(size);
};

// ─── Segmented bar (also used standalone) ──────────────────────────────────

export const SegmentedBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: 6px;
  margin-bottom: ${theme.spacing.xl};
`;

export const SegmentedItem = styled.button`
  background: ${({ $active }) => ($active ? theme.colors.primary : 'transparent')};
  border: none;
  border-radius: ${theme.radii.sm};
  padding: 10px ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#ffffff' : theme.colors.textSecondary)};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 40px;

  &:hover {
    background: ${({ $active }) => ($active ? theme.colors.primary : theme.colors.background)};
    color: ${({ $active }) => ($active ? '#ffffff' : theme.colors.text)};
  }

  ${theme.media.mobile} {
    padding: 8px ${theme.spacing.md};
    font-size: ${theme.typography.small.size};
  }
`;

// ─── Shared bits ───────────────────────────────────────────────────────────

const Caption = styled.div`
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
`;

const DetailCard = styled.section`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};

  ${theme.media.mobile} {
    padding: ${theme.spacing.md};
  }
`;

// The unframed detail, for items whose content brings its own cards (the
// Company Overview sections): heading, content and pager with no card around them.
const PlainDetail = styled.section`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const DetailTitle = styled.h2`
  margin: 0;
  font-size: ${theme.typography.h2.size};
  font-weight: ${theme.typography.h2.weight};
  line-height: ${theme.typography.h2.lineHeight};
  color: ${theme.colors.text};
  overflow-wrap: anywhere;
`;

const Position = styled.span`
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  color: ${theme.colors.textSecondary};
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  border-top: 1px solid ${theme.colors.borderLight};
  padding-top: ${theme.spacing.md};
`;

const PagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  max-width: 100%;
  background: ${({ $primary }) => ($primary ? theme.colors.primary : 'transparent')};
  border: 1.5px solid ${({ $primary }) => ($primary ? theme.colors.primary : theme.colors.border)};
  color: ${({ $primary }) => ($primary ? '#ffffff' : theme.colors.text)};
  border-radius: ${theme.radii.sm};
  padding: 10px ${theme.spacing.md};
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }

  &:hover {
    background: ${({ $primary }) => ($primary ? theme.colors.primaryDark : theme.colors.surfaceHover)};
  }
`;

const EmptyText = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.body.size};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: none;
  border: none;
  padding: 0;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${theme.colors.primary};
  cursor: pointer;
  min-height: 44px;

  &:hover {
    color: ${theme.colors.primaryDark};
  }
`;

// ─── List card (the rail, and the drill list) ──────────────────────────────

const RailLayout = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: flex-start;
`;

// Follows the scroll beside a tall detail, parked just under the sticky top
// bar, and scrolls inside itself when the list is long (a visible scrollbar:
// the whole point is that nothing hides). Needs every ancestor up to the
// window free of overflow hidden/auto (see Layout.styles, CompanyPage.styles).
const Rail = styled.nav`
  flex: 0 0 280px;
  min-width: 240px;
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-sizing: border-box;
  position: sticky;
  top: calc(${theme.layout.topBarHeight} + ${theme.spacing.md});
  max-height: calc(100vh - ${theme.layout.topBarHeight} - ${theme.spacing.xl});
`;

// The drill list: the same card at full width, growing with its content.
const ListCard = styled.nav`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-sizing: border-box;
`;

const RailToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding: 6px 6px 4px;
`;

const RailCaption = styled(Caption)`
  padding: 10px 12px 6px;
`;

const RailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  overflow-y: auto;
`;

const RailItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;
  background: ${({ $active }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active }) => ($active ? '#ffffff' : theme.colors.text)};
  border: none;
  border-radius: ${theme.radii.sm};
  padding: 12px 14px;
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  min-height: 44px;
  box-sizing: border-box;
  flex: 0 0 auto;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${({ $active }) => ($active ? theme.colors.primary : theme.colors.surfaceHover)};
  }
`;

// Up to three lines of a name; a long objection ends in an ellipsis here and
// reads in full in the detail.
const RailItemTitle = styled.span`
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RailItemCaption = styled.span`
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  line-height: 1.4;
  color: ${({ $active }) => ($active ? '#d6eef5' : theme.colors.textSecondary)};
`;

const RailDetail = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

// ─── Accordion ─────────────────────────────────────────────────────────────

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AccordionItem = styled.section`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  border-left: 4px solid ${({ $active }) => ($active ? theme.colors.primary : 'transparent')};
  overflow: hidden;
`;

const AccordionHeader = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 14px 16px;
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  cursor: pointer;
  min-height: 44px;
  box-sizing: border-box;
`;

const AccordionTitle = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const AccordionCaption = styled.span`
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  color: ${theme.colors.textSecondary};
`;

const AccordionBody = styled.div`
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Chevron = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={open ? theme.colors.primary : theme.colors.textSecondary} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 3 }}>
    {open ? <path d="m18 15-6-6-6 6" /> : <path d="m6 9 6 6 6-6" />}
  </svg>
);

const Arrow = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {dir === 'left' ? <><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></> : <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>}
  </svg>
);

// ─── The switcher ──────────────────────────────────────────────────────────

/**
 * items:        [{ key, label, caption? }]
 * activeKey:    the selected item's key
 * onChange:     (key) => void
 * renderDetail: (item, { index, count }) => node, the body shown for the active item
 * ariaLabel:    name of the list for assistive tech ("Offerings")
 * countLabel:   plural noun for the count caption ("offerings" -> "5 offerings")
 * countText:    exact count caption, when the caller filters ("7 of 64 objections")
 * toolbar:      node shown at the top of the list (search, filters); keeps the list visible for one item
 * emptyText:    shown when items is empty (after the toolbar)
 * pager:        show previous / next buttons under the detail
 * frame:        'card' (default) wraps the detail in a card; 'plain' when the content brings its own cards
 * position:     show "2 of 5" above the title (off for fixed sections, where it reads like a wizard step)
 * narrow:       layout when the column is too narrow for the rail: 'pills' (default) or 'drill'
 * phone:        layout at phone width: 'accordion' (default) or 'drill'
 * backLabel:    the drill detail's back button text
 * tier:         force 'rail' | 'pills' | 'accordion' | 'drill' (tests, storybook); normally measured
 */
const ItemSwitcher = ({
  items = [], activeKey, onChange, renderDetail, ariaLabel = 'Items', countLabel, countText: countTextProp,
  toolbar = null, emptyText = 'Nothing to show.', pager = true, frame = 'card', position = true,
  narrow = 'pills', phone = 'accordion', backLabel = 'Back to list', tier: forcedTier,
  // Optional veto on any selection change or accordion collapse (returns
  // false to keep the current item): a page with an unsaved form uses it.
  canChange,
}) => {
  const ref = useRef(null);
  const activeRef = useRef(null);
  const detailRef = useRef(null);
  const mounted = useRef(false);
  const focusAfterChange = useRef(false);
  const [drillOpen, setDrillOpen] = useState(false);
  // Accordion only. Starts closed: on a phone the page opens as a list of
  // names so the reader sees every section and picks one; a tap opens it and
  // tapping the open item shuts it again.
  const [accordionClosed, setAccordionClosed] = useState(true);
  const measured = useTier(ref, forcedTier);
  const tier = measured === 'pills' && narrow === 'drill' ? 'drill'
    : measured === 'accordion' && phone === 'drill' ? 'drill'
      : measured;
  const count = items.length;
  const activeIndex = Math.max(0, items.findIndex((it) => it.key === activeKey));
  const active = items[activeIndex];
  const prev = activeIndex > 0 ? items[activeIndex - 1] : null;
  const next = activeIndex < count - 1 ? items[activeIndex + 1] : null;
  const countText = countTextProp || (countLabel ? `${count} ${countLabel}` : null);
  const Frame = frame === 'plain' ? PlainDetail : DetailCard;

  // Keep the selected row in view when the list scrolls inside the rail, and
  // move focus to it after a keyboard move (roving tabindex).
  useEffect(() => {
    const el = activeRef.current;
    if (el && typeof el.scrollIntoView === 'function' && tier === 'rail') el.scrollIntoView({ block: 'nearest' });
    if (el && focusAfterChange.current) {
      focusAfterChange.current = false;
      el.focus();
    }
  }, [activeKey, tier]);

  // On a switch, bring the top of the detail into view: a reader scrolled deep
  // into one section who picks another in the floating rail would otherwise
  // land in the middle of the new one. Skipped on first render, and in a
  // layout box with no size (test DOM, hidden).
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const el = detailRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.height) return;
    const offset = parseInt(theme.layout.topBarHeight, 10) + 16;
    if (rect.top < offset && typeof window.scrollBy === 'function') window.scrollBy({ top: rect.top - offset, behavior: 'smooth' });
  }, [activeKey, drillOpen]);

  const change = (key) => {
    if (canChange && !canChange()) return;
    onChange(key);
  };

  // Arrow keys move the selection; Home / End jump; the list is one Tab stop.
  const onListKeyDown = (e) => {
    // The toolbar (a search box) sits inside the list: keys typed there move
    // the caret, never the selection.
    if (e.target !== e.currentTarget && /^(INPUT|TEXTAREA|SELECT)$/i.test(e.target.tagName)) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key) || count === 0) return;
    e.preventDefault();
    let nextIndex = activeIndex;
    if (e.key === 'ArrowDown') nextIndex = Math.min(count - 1, activeIndex + 1);
    if (e.key === 'ArrowUp') nextIndex = Math.max(0, activeIndex - 1);
    if (e.key === 'Home') nextIndex = 0;
    if (e.key === 'End') nextIndex = count - 1;
    if (nextIndex !== activeIndex) {
      focusAfterChange.current = true;
      change(items[nextIndex].key);
    }
  };

  const select = (key) => {
    if (canChange && !canChange()) return;
    onChange(key);
    setDrillOpen(true);
  };

  const detail = (item, index) => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {position && count > 1 && <Position>{`${index + 1} of ${count}`}</Position>}
        <DetailTitle>{item.label}</DetailTitle>
      </div>
      {renderDetail ? renderDetail(item, { index, count }) : null}
      {pager && count > 1 && (
        <Pager>
          <div style={{ minWidth: 0 }}>
            {prev && (
              <PagerButton type="button" onClick={() => change(prev.key)}>
                <Arrow dir="left" />
                <span>{prev.label}</span>
              </PagerButton>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            {next && (
              <PagerButton type="button" $primary onClick={() => change(next.key)}>
                <span>{next.label}</span>
                <Arrow dir="right" />
              </PagerButton>
            )}
          </div>
        </Pager>
      )}
    </>
  );

  const rows = (listKind) => items.map((item, i) => {
    const isSelected = i === activeIndex;
    const isActive = isSelected && listKind !== 'drill';
    return (
      <RailItem
        key={item.key}
        ref={isSelected ? activeRef : undefined}
        type="button"
        tabIndex={isSelected ? 0 : -1}
        $active={isActive}
        aria-current={isActive ? 'true' : undefined}
        title={item.label}
        onClick={() => select(item.key)}
      >
        <RailItemTitle>{item.label}</RailItemTitle>
        {item.caption && <RailItemCaption $active={isActive}>{item.caption}</RailItemCaption>}
      </RailItem>
    );
  });

  // Nothing (left) to show: the toolbar stays so the filter can be changed.
  if (count === 0) {
    return (
      <div ref={ref} data-testid="item-switcher-empty">
        <ListCard aria-label={ariaLabel}>
          {toolbar && <RailToolbar>{toolbar}</RailToolbar>}
          <EmptyText>{emptyText}</EmptyText>
        </ListCard>
      </div>
    );
  }

  if (count === 1 && !toolbar) {
    return (
      <div ref={ref}>
        <Frame ref={detailRef}>{detail(active, 0)}</Frame>
      </div>
    );
  }

  if (tier === 'drill') {
    return (
      <div ref={ref} data-testid="item-switcher-drill">
        {drillOpen && active ? (
          <>
            <BackButton type="button" onClick={() => setDrillOpen(false)}>
              <Arrow dir="left" />
              {backLabel}
            </BackButton>
            <Frame ref={detailRef} aria-label="Selected item">{detail(active, activeIndex)}</Frame>
          </>
        ) : (
          <ListCard aria-label={ariaLabel} onKeyDown={onListKeyDown}>
            {toolbar && <RailToolbar>{toolbar}</RailToolbar>}
            {countText && <RailCaption>{countText}</RailCaption>}
            {rows('drill')}
          </ListCard>
        )}
      </div>
    );
  }

  if (tier === 'accordion') {
    // Tapping the open item closes it (nothing open until the next tap); the
    // selection itself is kept, so the other layouts still show that item.
    const toggleAccordion = (item, open) => {
      if (canChange && !canChange()) return;
      if (open) {
        setAccordionClosed(true);
      } else {
        setAccordionClosed(false);
        onChange(item.key);
      }
    };
    return (
      <div ref={ref} data-testid="item-switcher-accordion">
        {toolbar && <RailToolbar style={{ padding: 0, marginBottom: 12 }}>{toolbar}</RailToolbar>}
        {countText && <Caption style={{ marginBottom: 12 }}>{countText}</Caption>}
        <AccordionList>
          {items.map((item, i) => {
            const open = i === activeIndex && !accordionClosed;
            return (
              <AccordionItem key={item.key} $active={open}>
                <AccordionHeader type="button" aria-expanded={open} onClick={() => toggleAccordion(item, open)}>
                  <AccordionTitle>
                    <span>{item.label}</span>
                    {item.caption && <AccordionCaption>{item.caption}</AccordionCaption>}
                  </AccordionTitle>
                  <Chevron open={open} />
                </AccordionHeader>
                {open && (
                  <AccordionBody>
                    {renderDetail ? renderDetail(item, { index: i, count }) : null}
                  </AccordionBody>
                )}
              </AccordionItem>
            );
          })}
        </AccordionList>
      </div>
    );
  }

  if (tier === 'rail') {
    return (
      <div ref={ref} data-testid="item-switcher-rail">
        <RailLayout>
          <Rail aria-label={ariaLabel} onKeyDown={onListKeyDown}>
            {toolbar && <RailToolbar>{toolbar}</RailToolbar>}
            {countText && <RailCaption>{countText}</RailCaption>}
            <RailList>{rows('rail')}</RailList>
          </Rail>
          <RailDetail>
            {active && <Frame ref={detailRef} aria-label="Selected item">{detail(active, activeIndex)}</Frame>}
          </RailDetail>
        </RailLayout>
      </div>
    );
  }

  // A bar of one item says nothing: with a toolbar and a single item, show the
  // toolbar and the detail alone (the rail keeps its one row, where the
  // toolbar lives inside it).
  return (
    <div ref={ref} data-testid="item-switcher-pills">
      {toolbar && <RailToolbar style={{ padding: 0, marginBottom: 12 }}>{toolbar}</RailToolbar>}
      {countText && <Caption style={{ marginBottom: 8 }}>{countText}</Caption>}
      {count > 1 && (
        <SegmentedBar role="group" aria-label={ariaLabel}>
          {items.map((item, i) => {
            const isActive = i === activeIndex;
            return (
              <SegmentedItem key={item.key} type="button" $active={isActive} aria-current={isActive ? 'true' : undefined} title={item.label} onClick={() => change(item.key)}>
                {item.label}
              </SegmentedItem>
            );
          })}
        </SegmentedBar>
      )}
      {active && <Frame ref={detailRef} aria-label="Selected item">{detail(active, activeIndex)}</Frame>}
    </div>
  );
};

export default ItemSwitcher;
